<template>
  <div
    class="border-gray flex flex-col gap-3 rounded-lg border bg-white p-3 shadow-md"
  >
    <router-link
      :to="{
        name: routerLinkName,
        params: { id: load.id },
      }"
      @click="handleSelectLoad"
    >
      <div :class="className">
        <div>
          <div class="mb-2 flex justify-between">
            <div
              v-if="isLoadsDashboard"
              class="flex flex-col items-start"
            >
              <img
                v-if="brokerLogo && showImage"
                class="flex h-4 items-center text-2xs"
                :src="brokerLogo"
                alt="broker logo"
                @error="handleImageError"
              />
              <p
                v-else
                class="font-semibold text-neutral-subtle"
              >
                {{ load.broker_name }}
              </p>
              <div class="mt-2 flex items-center justify-between">
                <p
                  v-if="load?.last_posted_at"
                  class="text-xs text-neutral-subtle"
                >
                  Posted {{ fromNowFirstUnit(load.last_posted_at) }} ago
                </p>
                <p
                  v-else
                  class="h-4"
                ></p>
              </div>
            </div>

            <div
              v-if="isLoadsDashboard"
              class="flex flex-col items-end"
            >
              <p class="text-lg font-semibold text-dark-gray">
                {{ formatPrice(load.price) }}
              </p>
              <div class="flex items-center gap-1">
                <span
                  v-if="loadRateText"
                  class="rounded px-2 text-xs"
                  :class="loadRateClass"
                  >{{ loadRateText }}</span
                >

                <p
                  v-if="ratePerMile"
                  class="text-xs text-neutral-subtlest"
                >
                  {{ ratePerMile }}/mi
                </p>
              </div>
            </div>
            <div v-else>
              <span
                class="rounded-full px-2 text-sm capitalize"
                :class="{
                  'bg-yellow-100 text-yellow-700': load.status === 'covered',
                  'bg-blue-100 text-blue-500': load.status === 'progress',
                  'bg-green-100 text-green-800':
                    load.status === 'assumed_delivered',
                  'bg-red-150 text-red-500':
                    load.status === 'assign_vehicle' ||
                    load.status === 'available',
                }"
              >
                {{ formatLoadStatus(load.status) }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <div class="flex gap-2">
              <img
                src="@/assets/origin-marker.svg"
                alt="origin icon"
              />

              <div class="flex flex-wrap items-center">
                <p class="mr-3 min-w-fit text-left">
                  {{ originText }}
                </p>

                <p
                  v-if="isLoadsDashboard"
                  class="text-left text-xs font-normal"
                >
                  {{
                    formatStopDate(
                      load?.pickup_time,
                      load?.pickup_time_timezone,
                      load?.pickup_has_time
                    )
                  }}
                </p>
              </div>
            </div>

            <p
              v-if="!isLoadsDashboard"
              class="font-normal"
            >
              {{
                formatStopDate(
                  load?.pickup_time,
                  load?.pickup_time_timezone,
                  load?.pickup_has_time
                )
              }}
            </p>
          </div>

          <div class="flex items-center gap-2">
            <img
              v-if="isLoadsDashboard"
              class="-mb-2 -mt-2.5 ml-[1px] mr-3 pl-2"
              src="@/assets/loads/line.svg"
              alt="line icon"
            />
            <img
              v-else
              class="mr-3 pl-2"
              src="@/assets/loads/line.svg"
              alt="line icon"
            />

            <span
              v-if="isLoadsDashboard"
              class="flex rounded-md bg-gray-100 px-1 py-0.5 text-sm"
            >
              <img
                class="text-2xs"
                src="@/assets/loads/down-arrow.svg"
                alt="arrow icon"
              />
              {{ billableMiles }} mi
            </span>

            <p class="">
              {{ load?.number_of_stops }} Stop{{
                load?.number_of_stops > 1 ? 's' : ''
              }}
            </p>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex gap-2">
              <img
                style="margin-left: -2px"
                src="@/assets/loads/destination-marker.svg"
                alt="destination icon"
              />

              <div class="flex flex-wrap items-center">
                <p class="mr-3 min-w-fit text-left">
                  {{ destinationText }}
                </p>

                <p
                  v-if="isLoadsDashboard"
                  class="text-xs font-normal"
                >
                  {{
                    formatStopDate(
                      load?.dropoff_time,
                      load?.dropoff_time_timezone,
                      load?.dropoff_has_time
                    )
                  }}
                </p>
              </div>
            </div>

            <p
              v-if="!isLoadsDashboard"
              class="font-normal"
            >
              {{
                formatStopDate(
                  load?.dropoff_time,
                  load?.dropoff_time_timezone,
                  load?.dropoff_has_time
                )
              }}
            </p>
          </div>
        </div>

        <div
          v-if="isLoadsDashboard"
          class="mt-4 flex flex-wrap gap-2"
        >
          <span
            v-for="type in equipmentTypes"
            :key="type"
            class="rounded-md bg-gray-100 px-2 text-sm"
            >{{ capitalizeWords(type) }}</span
          >
          <span class="rounded-md bg-gray-100 px-2 text-sm">{{
            loadWeight
          }}</span>
        </div>
        <div v-else-if="load.carrier_vehicle">
          <div class="flex gap-1">
            <img
              src="@/assets/blue-circle-check.svg"
              alt="blue check"
            />
            <p class="text-xs font-normal">
              {{
                load.carrier_vehicle?.id && load?.assigned_by_type
                  ? `Vehicle Assigned by ${capitalizeWords(load?.assigned_by_type)}`
                  : 'No carrier vehicle assigned'
              }}
            </p>
          </div>
          <div class="flex items-center justify-between">
            <p class="font-normal">Truck Number</p>
            <p>{{ load.carrier_vehicle.truck_number || '-' }}</p>
          </div>
          <div class="flex items-center justify-between">
            <p class="font-normal">VIN</p>
            <p>{{ load.carrier_vehicle.vin || '-' }}</p>
          </div>
        </div>
      </div>
    </router-link>

    <!-- <button-main
      v-if="!isLoadsDashboard && !load?.carrier_vehicle"
      class="text-1md !text-white"
      variant="secondary"
      custom-bg="#374053"
      @click="openAssignVehicleBottomSheet"
    >
      Assign Vehicle
    </button-main> -->
  </div>

  <AssignVehicleBottomSheet
    :visible="showAssignVehicleBottomSheet"
    @close="closeAssignVehicleBottomSheet"
  >
  </AssignVehicleBottomSheet>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import AssignVehicleBottomSheet from '@/components/loads/AssignVehicleBottomSheet.vue';
import { formatPrice } from '@/utils/currency';
import { fromNowFirstUnit } from '@/common/timestamps';
import {
  formatStopDate,
  formatLoadStatus,
  capitalizeWords,
} from '@/utils/loadBoardUtils.js';

export default {
  name: 'Load Detail',
  components: {
    AssignVehicleBottomSheet,
  },
  props: {
    load: Object,
    className: String,
    isLoadsDashboard: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const store = useStore();
    const showAssignVehicleBottomSheet = ref(false);

    const openAssignVehicleBottomSheet = () => {
      showAssignVehicleBottomSheet.value = true;
    };

    const closeAssignVehicleBottomSheet = () => {
      showAssignVehicleBottomSheet.value = false;
    };

    const firstStop = computed(() => props.load?.stops?.[0]);

    const originText = computed(() => {
      const city =
        firstStop?.value?.city || firstStop?.value?.area_five_zip_city;
      const state =
        firstStop?.value?.state || firstStop?.value?.area_five_zip_state;

      return `${city}${state ? ', ' + state : ''}`;
    });
    const destinationText = computed(() => {
      const city = lastStop?.value?.city || lastStop?.value?.area_five_zip_city;
      const state =
        lastStop?.value?.state || lastStop?.value?.area_five_zip_state;

      return `${city}${state ? ', ' + state : ''}`;
    });

    const lastStop = computed(
      () => props.load?.stops?.[props.load?.stops?.length - 1]
    );

    const routerLinkName = computed(() => 'Load Detail');

    const billableMiles = computed(() => {
      const distance = props?.load?.posted_miles;

      return Math.round(distance * 100) / 100;
    });

    const equipmentTypes = computed(
      () => props.load?.posted_equipment_types?.slice(0, 2) || []
    );

    const weightUnit = {
      pound: 'lbs',
      kilogram: 'kg',
    };

    const loadWeight = computed(
      () =>
        `${props.load?.weight?.toLocaleString('en-US')} ${weightUnit[props.load?.weight_unit]}`
    );

    const brokerLogo = computed(() => {
      if (props?.load?.broker_name === 'WWEX Group') {
        return 'https://highway-prod-monitor.s3.amazonaws.com/6sxe224o6nv3xvwwmh3odrdajdfm?response-content-disposition=inline%3B%20filename%3D%22WWEXGroup_GTM_Navy.png%20FOR%20USE.png%22%3B%20filename%2A%3DUTF-8%27%27WWEXGroup_GTM_Navy.png%2520FOR%2520USE.png&response-content-type=image%2Fpng&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIA5TETHIMSP4TR2AXM%2F20250528%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250528T134438Z&X-Amz-Expires=3600&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEK7%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDot1sCYdPmdLXl8sqf4AtP0A7VnfMhQ73IZikhCIkjlwIhALvVBHdkM%2BoScExMe7XQWQk9CI%2FrouxYGJdB4gZX9HNRKu8DCHcQAhoMOTM0NDY0MTQ0MTY0Igx2%2FD43i1qiI5yDKyAqzAPvS3JB2sFf%2FSLASNGpuBJXCPEcntIoArdmgrwvC1idwxxrkg8AJcx45CybhEctnKPghZVrIR2tNukzndw%2Fwzv5NVmsRHwQNUPdswCPaIgpDnOg6P9Ob%2FU9wMULIpEbWQ3MqM9JPyhxu5UCired1R0auqgLEA3f5lVxGXFadXTwb1yUljXU%2BhNirVWQHkUezfVkUzoktylXVJHPzS9QrJES6LSg8FU8mUGaBe3MjZJYE8laVuqvuCqdEvaZGRWmEXza%2BUDB2KYsCGGvwCphFYyqTeOIzKP9mf6PFeAQa6dgAjgoX3ooF%2Ff9HEAwp4QAcN0P9%2FJXvRNX5vGXR6R0l66wiSEijnsOzNzl7xG75NSpWSTAOEMfiOHdqPUpTF9Tr5GRzTnxemz4JmzGdViJXlt9%2Bmn7Myrwe%2FOG6y1V%2FiUinAUE%2BTfsBrqwIt%2BEszUmhwEjBhfaMYFRGHW4wFq4xNy1r6wYYLhq34OjuulSQ2ySmLFolnWPTmgMWdox5wTtAXBvTI7FR9YqonWS%2FCGBYXYfbRpw9JyWNhzx1i%2FUJezrjfjLzMjrRa8Jj9zDF4v3e52BF3oBHIr2PiLfAyPDWXWVWEFHGxotOxRrnpidMJuc3MEGOqQBkrvicw5Ud1N%2FY6hYeSx%2FI8Rrutdij2UhJaoS3qf%2FodgLau9R5XT6ZR2YTygV5JA2%2FtBWJoqPXBuUymi3u5xKAK0qKiieZsduBloPf9Hfed34NiasbQ5Af1UQLvjofjQWyIFem8IlaQyBU6%2BLsLfs43MN%2B5i0JSWhAgWfCUPBCpkuuibyG8E9Ntq7Q7lfFimbR7QXzqfCTI0hgNIfD51NVEdWZ%2FE%3D&X-Amz-SignedHeaders=host&X-Amz-Signature=9a498249ca4e60823502820185fcdc2ed206d75ff814353ee0e35e6aaaccd688';
      }

      return props?.load?.broker_logo?.url;
    });

    const loadRate = computed(() => {
      return props?.load?.lane_prediction?.posted_rate_assessment;
    });

    const loadRateClass = computed(() => {
      switch (loadRate?.value) {
        case 'Great':
          return 'bg-aqua-700 text-white';
        case 'Good':
          return 'bg-aqua-500 text-white';
        case 'Fair':
          return 'bg-aqua-300 text-steel-1100';
        case 'Below':
          return 'bg-red-100 text-red-600';
        default:
          return '';
      }
    });
    const loadRateText = computed(() => {
      return null;
      // switch (loadRate?.value) {
      //   case 'Great':
      //     return 'Great Deal';
      //   case 'Good':
      //     return 'Good Deal';
      //   default:
      //     return null;
      // }
    });

    const showImage = ref(true);
    const handleImageError = () => {
      if (showImage?.value) {
        showImage.value = false;
      }
    };

    const ratePerMile = computed(() => {
      if (!props?.load?.lane_prediction && !props?.load?.rate_per_mile)
        return null;

      return formatPrice(props?.load?.rate_per_mile);
    });

    const handleSelectLoad = () => {
      if (store.state.users.featureFlags?.carrier_loadboard_prefetch?.enabled) {
        store.commit('loads/setPreLoadResponse', props?.load);
        store.commit('loads/setLoadDetail', {
          ...props?.load,
          stops: props?.load?.stops?.map((stop) => ({
            ...stop,
            latitude_for_map: stop?.lat,
            longitude_for_map: stop?.lon,
          })),
        });
      }
    };

    return {
      showAssignVehicleBottomSheet,
      openAssignVehicleBottomSheet,
      closeAssignVehicleBottomSheet,
      fromNowFirstUnit,
      routerLinkName,
      equipmentTypes,
      billableMiles,
      formatPrice,
      loadWeight,
      brokerLogo,
      firstStop,
      lastStop,
      loadRate,
      loadRateClass,
      loadRateText,
      formatStopDate,
      formatLoadStatus,
      showImage,
      handleImageError,
      capitalizeWords,
      ratePerMile,
      handleSelectLoad,
      originText,
      destinationText,
    };
  },
};
</script>
